/**
 * Template Settings Page
 * Displays all predefined WhatsApp templates with their Meta approval status.
 * Status flow: PENDING â†’ IN_REVIEW â†’ APPROVED
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPredefinedStatus,
  applyTemplate,
  refreshTemplateStatus,
  openEditModal,
  closeEditModal,
  updateTemplate
} from '../store/slices/templateSettingsSlice';
import EditTemplateModal from '../components/EditTemplateModal';
import './TemplateSettings.css';
import { fetchBusinessAccounts } from '../store/slices/businessAccountsSlice';


const STATUS_CONFIG = {
  PENDING: {
    label: 'Pending',
    className: 'status-pending',
    description: 'Not yet submitted to Meta'
  },
  IN_REVIEW: {
    label: 'Waiting for Review',
    className: 'status-in-review',
    description: 'Submitted to Meta ” awaiting approval'
  },
  APPROVED: {
    label: 'Approved',
    className: 'status-approved',
    description: 'Approved by Meta'
  },
  REJECTED: {
    label: 'Rejected',
    className: 'status-rejected',
    description: 'Rejected by Meta'
  },
  DRAFT: {
    label: 'Draft',
    className: 'status-draft',
    description: 'Draft ” not submitted'
  }
};

const TYPE_COLORS = {
  INVOICE: '#4CAF50',
  INITIAL_MESSAGE: '#2196F3',
  WELCOME_MESSAGE: '#00BCD4',
  FEEDBACK_REQUEST: '#FF9800',
  PROFILE_UPDATE: '#9C27B0',
  APPOINTMENT_BOOKING: '#E91E63',
  PRODUCT: '#FF5722',
  IMAGE: '#607D8B',
  CAROUSEL_PRODUCT: '#795548',
  FEEDBACK_FLOW: '#F44336',
  APPOINTMENT_FLOW: '#3F51B5',
  PROFILE_UPDATE_FLOW: '#009688'
};

function typeColor(type) {
  return TYPE_COLORS[type] || '#757575';
}

function typeInitial(name) {
  return (name || '?').charAt(0).toUpperCase();
}


const TemplateSettings = () => {
  const dispatch = useDispatch();
  const { currentAccount, accounts } = useSelector(state => state.businessAccounts);
  // Use the actively selected account; fall back to the first available account
  const account = currentAccount || (accounts && accounts.length > 0 ? accounts[0] : null);
  const {
    predefinedTemplates,
    loading,
    error,
    applyingTemplateId,
    isEditModalOpen,
    selectedItem
  } = useSelector(state => state.templateSettings);

  const [applyError, setApplyError] = useState(null);

  // Ensure business accounts are loaded (AdminLayout also does this, but guard here too)
  useEffect(() => {
    dispatch(fetchBusinessAccounts());
  }, [dispatch]);

  // Load predefined templates with their status on mount / account change
  useEffect(() => {
    if (account?.wabaId) {
      dispatch(fetchPredefinedStatus(account.wabaId)).unwrap().catch(err => {
        console.error('Failed to load predefined templates:', err);
      });
    }
  }, [dispatch, account?.wabaId]);

  // â”€â”€ actions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleApply = async (templateId) => {
    if (!account?.wabaId) return;
    setApplyError(null);
    try {
      await dispatch(applyTemplate({ templateId })).unwrap();
    } catch (err) {
      setApplyError(err?.message || 'Failed to apply template');
    }
  };

  const handleRefreshStatus = async (templateId) => {
    setApplyError(null);
    try {
      await dispatch(refreshTemplateStatus(templateId)).unwrap();
    } catch (err) {
      setApplyError(err?.message || 'Failed to check template status');
    }
  };

  const handleEdit = (predefined) => {
    if (predefined.record) {
      dispatch(openEditModal(predefined.record));
    }
  };

  
  if (loading && predefinedTemplates.length === 0) {
    return (
      <div className="template-settings-container">
        <div className="ts-loading">
          <div className="ts-spinner" />
          <p>Loading templates¦</p>
        </div>
      </div>
    );
  }

  return (
    <div className="template-settings-container">
      {/* Header */}
      <div className="template-settings-header">
        <h1>WhatsApp Template Settings</h1>
        <p className="subtitle">
          Manage predefined message templates for your WhatsApp Business Account
        </p>
      </div>

      {/* Global error */}
      {(error || applyError) && (
        <div className="error-banner">
          <span>{applyError || error?.message || 'An error occurred'}</span>
          <button
            className="ts-dismiss"
            onClick={() => setApplyError(null)}
            aria-label="Dismiss"
          >
            âœ•
          </button>
        </div>
      )}

      {/* No WABA warning */}
      {!account?.wabaId && (
        <div className="ts-no-waba">
          <p>No WhatsApp Business Account connected. Please complete onboarding first.</p>
        </div>
      )}

      {/* Template grid */}
      <div className="ts-grid-wrapper">
        {predefinedTemplates.length === 0 && !loading ? (
          <div className="ts-empty">
            <p>No predefined templates found.</p>
          </div>
        ) : (
          <div className="ts-grid">
            {predefinedTemplates.map(tpl => {
              const statusCfg = STATUS_CONFIG[tpl.status] || STATUS_CONFIG.PENDING;
              const isApplying = applyingTemplateId === tpl.id;
              const canApply = ['PENDING', 'DRAFT', 'REJECTED'].includes(tpl.status);
              const inReview = tpl.status === 'IN_REVIEW';
              const isApproved = tpl.status === 'APPROVED';
              const canCheckStatus = !!tpl.metaTemplateId && ['PENDING', 'IN_REVIEW'].includes(tpl.status);
              const isCheckingStatus = applyingTemplateId === tpl.metaTemplateId;

              return (
                <div key={tpl.id} className="ts-card">
                  {/* Card header */}
                  <div className="ts-card-header">
                    <div
                      className="ts-card-icon"
                      style={{ backgroundColor: typeColor(tpl.type) }}
                      aria-hidden="true"
                    >
                      {typeInitial(tpl.name)}
                    </div>
                    <div className="ts-card-title">
                      <h3>{tpl.name}</h3>
                      <p className="ts-card-category">{tpl.category || 'UTILITY'}</p>
                    </div>
                  </div>

                  {/* Description */}
                  {tpl.description && (
                    <p className="ts-card-desc">{tpl.description}</p>
                  )}

                  {/* Status badge */}
                  <div className="ts-card-status">
                    <span className={`ts-status-badge ${statusCfg.className}`}>
                      {statusCfg.label}
                    </span>
                    <span className="ts-status-desc">{statusCfg.description}</span>
                  </div>

                  {/* Meta submit error */}
                  {tpl.metaSubmitError && (
                    <div className="ts-meta-error">
                      <span>{tpl.metaSubmitError}</span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="ts-card-actions">
                    {canApply && (
                      <button
                        className="ts-btn ts-btn-primary"
                        onClick={() => handleApply(tpl.id)}
                        disabled={isApplying || !account?.wabaId}
                      >
                        {isApplying ? (
                          <>
                            <span className="ts-btn-spinner" />
                            Submitting¦
                          </>
                        ) : (
                          'Apply'
                        )}
                      </button>
                    )}

                    {inReview && (
                      <span className="ts-badge-review">
                        Waiting for Meta review
                      </span>
                    )}

                    {canCheckStatus && (
                      <button
                        className="ts-btn ts-btn-secondary"
                        onClick={() => handleRefreshStatus(tpl.metaTemplateId)}
                        disabled={isCheckingStatus || !account?.wabaId}
                      >
                        {isCheckingStatus ? (
                          <>
                            <span className="ts-btn-spinner" />
                            Checking¦
                          </>
                        ) : (
                          'Check Status'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit modal ” only for APPROVED templates */}
      {isEditModalOpen && selectedItem && (
        <EditTemplateModal
          template={selectedItem}
          onClose={() => dispatch(closeEditModal())}
        />
      )}
    </div>
  );
};

export default TemplateSettings;
